import { AsyncPipe, isPlatformServer } from '@angular/common';
import { Component, Inject, OnDestroy, PLATFORM_ID, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterOutlet } from '@angular/router';
import {
  AssetSectionDto,
  EmbedSectionDto,
  GalleryDto,
  GalleryPublicAccessLevel,
  GallerySectionDto,
  SectionDto,
  TenantDto,
  TextSectionDto,
} from '@picflow/types';
import { Observable, Subscription, distinctUntilChanged, map, switchMap } from 'rxjs';
import { GalleryTreeComponent } from '../../../gallery-shared/components/gallery-tree/gallery-tree.component';
import { MadeWithPicflowButtonComponent } from '../../../gallery-shared/components/made-with-picflow-button/made-with-picflow-button.component';
import { VideoViewerComponent } from '../../../gallery-shared/components/video-viewer/video-viewer.component';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { ApprovalLimitInfoComponent } from '../../../gallery/components/approval-limit-info/approval-limit-info.component';
import { GalleryPictureComponent } from '../../../gallery/components/gallery-picture/gallery-picture.component';
import { VideoOriginPipe } from '../../../gallery/pipes/video-origin.pipe';
import { GROWTHBOOK } from '../../../growthbook';
import { isDarkColor } from '../../../lib/is-dark-color';
import { loadFont } from '../../../lib/load-font';
import { parseColor } from '../../../lib/parse-color';
import { preloadFont } from '../../../lib/preload-font';
import { sleep } from '../../../lib/sleep';
import { AssetService } from '../../../services/asset.service';
import { CleanUserGeneratedHtmlService } from '../../../services/clean-user-generated-html.service';
import { GalleryService } from '../../../services/gallery.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { GalleryDownloadMenuOptionsComponent } from '../../../shared-extra/components/gallery-download-menu-options/gallery-download-menu-options.component';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { TenantLogoAndContactsComponent } from '../../../shared/components/tenant-logo-and-contacts/tenant-logo-and-contacts.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryCoverUrlPipe } from '../../../shared/pipes/gallery-cover-url.pipe';
import { SortByPipe } from '../../../shared/pipes/sort-by.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { AlbumSectionComponent } from '../../components/album-section/album-section.component';
import { AssetsSectionComponent } from '../../components/assets-section/assets-section.component';
import { EmbedSectionComponent } from '../../components/embed-section/embed-section.component';
import { TenantIconLoaderComponent } from '../../components/tenant-icon-loader/tenant-icon-loader.component';
import { TextSectionComponent } from '../../components/text-section/text-section.component';
import { ViewFilterComponent } from '../../components/view-filter/view-filter.component';
import { ViewGalleryHeaderComponent } from '../../components/view-gallery-header/view-gallery-header.component';
import { ViewProfileMenuComponent } from '../../components/view-profile-menu/view-profile-menu.component';

@Component({
  selector: 'app-view-gallery-page',
  templateUrl: './view-gallery-page.component.html',
  styleUrls: ['./view-gallery-page.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    ButtonDirective,
    RouterLink,
    SvgComponent,
    ContextMenuComponent,
    GalleryTreeComponent,
    ViewProfileMenuComponent,
    GalleryDownloadMenuOptionsComponent,
    ViewFilterComponent,
    AlbumSectionComponent,
    TextSectionComponent,
    AssetsSectionComponent,
    EmbedSectionComponent,
    TooltipDirective,
    MadeWithPicflowButtonComponent,
    TenantIconLoaderComponent,
    AsyncPipe,
    SortByPipe,
    GalleryCoverUrlPipe,
    TranslatePipe,
    AvatarPipe,
    VideoViewerComponent,
    ViewGalleryHeaderComponent,
    TenantLogoAndContactsComponent,
    ApprovalLimitInfoComponent,
    AsyncPipe,
    VideoOriginPipe,
    GalleryPictureComponent,
  ],
})
export class ViewGalleryPageComponent implements OnDestroy {
  gallery: GalleryDto;
  tenant: TenantDto;
  sections$: Observable<SectionDto[]> = this.route.parent.data.pipe(
    map(data => data.gallery.id),
    distinctUntilChanged(),
    switchMap(galleryId => this.sectionService.getSections(galleryId))
  );
  totalSize = 0;
  platformBrowser = false;
  parentGallery: GalleryDto;
  url: string;
  routeDataSub: Subscription;
  accessLevel: GalleryPublicAccessLevel;
  updateSub: Subscription;
  galleryDescription: string;
  filters: Filter[] = [];
  noAssetsResult: boolean;
  @ViewChildren(AssetsSectionComponent) assetsSections: QueryList<AssetsSectionComponent>;
  @ViewChild('header') header: ViewGalleryHeaderComponent;
  darkGalleryBackground: boolean;
  public growthBook = inject(GROWTHBOOK);

  constructor(
    private route: ActivatedRoute,
    public state: StateService,
    private galleryService: GalleryService,
    private assetService: AssetService,
    private sectionService: SectionService,
    private cleanUserGeneratedHtmlService: CleanUserGeneratedHtmlService,

    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.routeDataSub = this.route.parent.data.subscribe(async data => {
      this.accessLevel = data.accessLevel;
      this.gallery = data.gallery;
      this.tenant = this.gallery['tenant'];
      if (isPlatformServer(this.platformId)) {
        this.preloadFonts();
        return;
      }
      this.url = window.location.href;
      console.debug(this.gallery); // Because in production the gallery is not accessible due to SSR
      this.platformBrowser = true;
      this.galleryDescription = this.cleanUserGeneratedHtmlService.clean(this.gallery.description);
      this.calcDarkBackground();
      if (this.gallery.default_title_typography?.font_family) {
        loadFont(this.gallery.default_title_typography.font_family);
      }
      if (this.gallery.default_paragraph_typography?.font_family) {
        loadFont(this.gallery.default_paragraph_typography.font_family);
      }

      if (this.gallery.cover.title_typography?.font_family) {
        loadFont(this.gallery.cover.title_typography.font_family);
      }
      if (this.gallery.cover.paragraph_typography?.font_family) {
        loadFont(this.gallery.cover.paragraph_typography.font_family);
      }
    });

    this.updateSub = this.galleryService.onUpdates.subscribe(async event => {
      if (!event) return;
      if (!this.gallery) return;
      if (event[1] !== this.gallery.id) return;
      if (!event[2]) return; // we only want external updates
      if (event[0] === 'deleted') location.reload();
      if (event[0] === 'updated') {
        // this.sectionService.fetchAndApplySectionUpdates(this.sections, this.gallery.id);
        const newGallery = await this.galleryService.checkForExternalUpdate(this.gallery, event[2]);
        if (newGallery) this.gallery = newGallery;
        this.calcDarkBackground();
      }
    });
  }

  async getDownloadSizeInKb(gallery: GalleryDto): Promise<number> {
    const assets = await this.assetService.getAssetsForGallery(gallery.id);
    return assets.reduce((t, asset) => t + asset.version_data.size / 1024, 0);
  }

  async getTotalDownloadSizeInKb(galleries: GalleryDto[]) {
    const sizes = await Promise.all(galleries.map(g => this.getDownloadSizeInKb(g)));
    return sizes.reduce((t, size) => t + size, 0);
  }

  toAssetSection(section: SectionDto) {
    return <AssetSectionDto>section;
  }
  toAlbumSection(section: SectionDto) {
    return <GallerySectionDto>section;
  }
  toTextSection(section: SectionDto) {
    return <TextSectionDto>section;
  }
  toEmbedSection(section: SectionDto) {
    return <EmbedSectionDto>section;
  }

  async onFilter(filters: Filter[]) {
    this.filters = filters;
    await sleep(0);
    this.noAssetsResult = !this.assetsSections.some(section => section && (!section.items || !!section.items.length));
  }

  resetFilter() {
    console.log('reset filter', this.header?.viewFilter);
    this.header?.viewFilter?.resetFilter();
  }

  niceSite(site: string) {
    if (!site) return '';
    if (site.split('//').length === 1) return site;
    return site.split('//')[1];
  }

  calcDarkBackground() {
    const rgb = parseColor(this.gallery.layout_background_color) || { r: 255, g: 255, b: 255 };
    this.darkGalleryBackground = isDarkColor(rgb.r, rgb.g, rgb.b);
  }

  getFontSize(size: number) {
    if (size === 1) return 'small';
    if (size === 2) return 'medium';
    if (size === 3) return 'large';
    if (size === 4) return 'huge';
  }

  preloadFonts() {
    if (!this.gallery) return;
    if (this.gallery.default_title_typography?.font_family) {
      preloadFont(this.gallery.default_title_typography?.font_family);
    }
    if (this.gallery.default_paragraph_typography?.font_family) {
      preloadFont(this.gallery.default_paragraph_typography?.font_family);
    }
    if (this.gallery.cover.title_typography?.font_family) {
      preloadFont(this.gallery.cover.title_typography?.font_family);
    }
    if (this.gallery.cover.paragraph_typography?.font_family) {
      preloadFont(this.gallery.cover.paragraph_typography?.font_family);
    }
  }

  ngOnDestroy(): void {
    this.routeDataSub.unsubscribe();
    this.updateSub?.unsubscribe();
  }
}
